/**
 * Which of the learner's notifications still deserve an OS notification, and
 * the record of the ones that already got one.
 *
 * All of the decision logic is here as plain functions so it can be tested
 * without a React tree, an Apollo client or an Electron runtime. The hook that
 * drives it is in `use-notification-poller.ts`.
 */

import type { DesktopNotification } from "./bridge";

/** The subset of `NotificationType` the poller selects. */
export interface LearnerNotification {
  id: string;
  title: string;
  message?: string | null;
  dismissed?: boolean | null;
}

/**
 * Where the ids already notified about are kept.
 *
 * `localStorage` rather than the keychain: this is a record of what the user
 * has been shown, not a credential, and it persists per origin, which under
 * the desktop shell is the same origin every launch.
 */
export const SEEN_IDS_KEY = "wone_desktop_seen_notifications";

/**
 * How many ids are retained. A learner's notification list is short and the
 * cap only exists so the entry cannot grow without bound over years of use.
 * Trimming keeps the most recent, which are the ones a re-notify would be
 * most visible on.
 */
export const SEEN_IDS_LIMIT = 500;

/**
 * The ids already notified about.
 *
 * Returns `null`, not an empty set, when there is no usable record. The two
 * cases must not be confused: no record means "first run, notify about
 * nothing", whereas an empty record means "nothing has been notified yet, so
 * everything is new". Collapsing them would fire a toast for every unread
 * notification the learner has ever had the first time the app starts.
 */
export function loadSeenIds(storage: Pick<Storage, "getItem"> = localStorage): Set<string> | null {
  let raw: string | null;
  try {
    raw = storage.getItem(SEEN_IDS_KEY);
  } catch {
    // Private mode, blocked site data, or an extension. Treated as "no
    // record", which is the quiet answer rather than the noisy one.
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return null;
  }
}

export function saveSeenIds(
  ids: readonly string[],
  storage: Pick<Storage, "setItem"> = localStorage
): void {
  const trimmed = ids.slice(-SEEN_IDS_LIMIT);
  try {
    storage.setItem(SEEN_IDS_KEY, JSON.stringify(trimmed));
  } catch {
    // A failed write costs a repeated notification, not a broken app. The
    // next successful write catches up.
  }
}

/**
 * The notifications that should raise an OS notification now.
 *
 * Dismissed rows never qualify: the learner has already dealt with them
 * somewhere else, and surfacing them again on the desktop would be the app
 * arguing with itself.
 */
export function selectUnnotified(
  notifications: readonly LearnerNotification[],
  seen: Set<string> | null
): LearnerNotification[] {
  if (seen === null) return [];
  return notifications.filter((n) => n.dismissed !== true && !seen.has(n.id));
}

/** Every id currently known, so a dismissed row is still recorded as seen and
 *  cannot come back as new if it is later un-dismissed. */
export function allIds(notifications: readonly LearnerNotification[]): string[] {
  return notifications.map((n) => n.id);
}

/**
 * The OS notification for one row.
 *
 * `message` is optional on the schema, so the title is the fallback body: a
 * notification with an empty body renders as a bare app name on macOS, which
 * tells the user nothing.
 */
export function toDesktopNotification(
  notification: LearnerNotification,
  deepLink: string
): DesktopNotification {
  return {
    id: notification.id,
    title: notification.title,
    body: notification.message ?? notification.title,
    deepLink,
  };
}
